import Shimmer from '@/components/ui/Shimmer';
import { SearchResultBlockLoadingShimmer } from '@/components/blocks/CreditInfo/snippets/LoadingShimmers';
import { memo } from 'react';

interface ComponentLazyShimmerProps {
  componentType?: string;
  creditInfoLoadingLayout?: boolean;
}

const homeCarouselSlideTypes = new Set([
  'EditorialSlideBlock',
  'FullSizeSlideBlock',
  'LiveNowSlideBlock',
  'LiveSliceBlock',
  'OnDemandSliceBlock',
  'PodcastProgramSlide',
  'TwoUpSlideBlock'
]);

const _ComponentLazyShimmer = ({
  componentType,
  creditInfoLoadingLayout
}: ComponentLazyShimmerProps): JSX.Element => {
  if (componentType === 'SearchResultBlock' && creditInfoLoadingLayout) {
    return (
      <div data-credit-info-search-loading>
        <SearchResultBlockLoadingShimmer />
      </div>
    );
  }

  if (componentType === 'LinkBlock' && creditInfoLoadingLayout) {
    return <Shimmer className="h-16 w-full rounded-lg" />;
  }

  if (componentType === 'TaxonomyTabContainerBlock') {
    return (
      <div className="container min-h-screen py-16">
        <Shimmer className="mb-14 h-14 w-52" />
        <div className="mb-16 flex gap-6 border-b border-gray-light pb-4">
          {Array.from({ length: 5 }, (_, index) => (
            <Shimmer className="h-5 min-w-24" key={`browse-tab-${index}`} />
          ))}
        </div>
        <div className="space-y-2">
          {Array.from({ length: 24 }, (_, index) => (
            <Shimmer className="h-12 w-full rounded-lg" key={`browse-row-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  if (componentType && homeCarouselSlideTypes.has(componentType)) {
    return (
      <div className="min-h-[600px] w-full sm:min-h-[809px] lg:min-h-[550px]">
        <Shimmer className="size-full min-h-[600px] sm:min-h-[809px] lg:min-h-[550px]" />
      </div>
    );
  }

  if (componentType === 'MyLibraryBlock') {
    return (
      <div className="module-spacing w-full overflow-x-clip" data-home-component-loading="library">
        <div className="container flex flex-col gap-8">
          <Shimmer className="h-10 w-48" />
          <div className="flex gap-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Shimmer className="h-14 w-40 rounded-full" key={`library-tab-${index}`} />
            ))}
          </div>
          <div className="flex gap-6 overflow-hidden" data-loading-card-row="product">
            {Array.from({ length: 4 }, (_, index) => (
              <Shimmer
                className="h-[400px] min-w-[75%] rounded-lg sm:min-w-[37.5%] md:min-w-[calc(25%-18px)]"
                key={`library-card-${index}`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (componentType === 'CreditPlanBlock') {
    return (
      <div
        className="container module-spacing w-full overflow-x-clip"
        data-home-component-loading="credit-plan"
      >
        <Shimmer className="mb-4 h-10 w-2/3 max-w-lg" />
        <Shimmer className="mb-8 h-5 w-full max-w-2xl" />
        <div className="flex gap-6 overflow-hidden" data-credit-plan-loading-cards>
          {Array.from({ length: 4 }, (_, index) => (
            <Shimmer
              className="aspect-[0.67] w-[260px] shrink-0 rounded-lg sm728:w-[410px] 2xl:w-[490px]"
              key={`credit-plan-card-${index}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container w-full py-5">
      <Shimmer className="h-10 w-1/3" />
      <Shimmer className="my-5 h-8 w-1/2" />
      <Shimmer className="h-48 w-full" />
    </div>
  );
};

const ComponentLazyShimmer = memo(_ComponentLazyShimmer);

ComponentLazyShimmer.displayName = 'ComponentLazyShimmer';
export default ComponentLazyShimmer;
